export type CustomElementModuleLoader = () => Promise<unknown>;
export type CustomElementManifest = Record<string, CustomElementModuleLoader>;

// These manifests are the explicit bridge between a custom element tag name
// and the dynamic import that registers it.
//
// We keep them entrypoint-specific so public pages do not pull in admin-only
// components, and we leave child-only elements out when their parent component
// already imports and registers them directly.

// Elements used by both public and admin entrypoints.
export const sharedCustomElementModules = {
  'tmp-confirm-submit': () => import('./confirm_submit_component.js'),
  'tmp-datawrapper-embed': () => import('./datawrapper_embed_component.js'),
} as const satisfies CustomElementManifest;

// Elements loaded by the public app and component preview entrypoints.
export const publicCustomElementModules = {
  ...sharedCustomElementModules,
  'tmp-animation': () => import('./animation_component.js'),
  'tmp-audio-player': () => import('./audio_player_component.js'),
  'tmp-captcha-protected-form': () => import('./captcha_protected_form.js'),
  'tmp-checkbox-counter': () =>
    import('./signup/checkbox_counter_component.ts'),
  'tmp-disclosure': () => import('./disclosure_component.js'),
  'tmp-footnote': () => import('./footnote_component.js'),
  'tmp-sensitive-content-component': () =>
    import('./shortcode/sensitive_content_component.js'),
  'tmp-series-page': () => import('./series_page_component.js'),
  'tmp-toast': () => import('./house_ad/toast_component.js'),
} as const satisfies CustomElementManifest;

// Elements loaded by the admin entrypoint.
export const adminCustomElementModules = {
  ...sharedCustomElementModules,
  'tmp-auto-submit': () => import('./forms/auto_submit_component.ts'),
  'tmp-callout-button': () => import('./forms/shortcode_toolbar_component.js'),
  'tmp-character-count': () => import('./forms/character_count_component.js'),
  'tmp-color-input': () => import('./forms/color_input_component.js'),
  'tmp-copy-table': () => import('./admin/copy_table_component.js'),
  'tmp-date-time-picker': () => import('./forms/date_time_picker_component.ts'),
  'tmp-dialog-modal': () => import('./forms/dialog_modal_component.js'),
  'tmp-disjoint-fieldsets': () =>
    import('./forms/disjoint_fieldsets_component.js'),
  'tmp-dropcap-button': () => import('./forms/shortcode_toolbar_component.js'),
  'tmp-expandable-list': () => import('./forms/expandable_list_component.js'),
  'tmp-file-upload': () => import('./forms/file_upload_component.ts'),
  'tmp-filter-button': () => import('./forms/shortcode_toolbar_component.js'),
  'tmp-lead-in-button': () => import('./forms/shortcode_toolbar_component.js'),
  'tmp-link-button-button': () =>
    import('./forms/shortcode_toolbar_component.js'),
  'tmp-listbox': () => import('./forms/listbox_component.js'),
  'tmp-listbox-option': () => import('./forms/listbox_option_component.js'),
  'tmp-markdown-editor': () => import('./forms/markdown_editor_component.js'),
  'tmp-optional-fieldset': () =>
    import('./forms/optional_fieldset_component.js'),
  'tmp-optional-text-change': () =>
    import('./forms/optional_text_change_component.js'),
  'tmp-partner-button': () => import('./forms/shortcode_toolbar_component.js'),
  'tmp-photo-button': () => import('./forms/shortcode_toolbar_component.js'),
  'tmp-photo-picker': () => import('./forms/photo_picker_component.js'),
  'tmp-placement-button': () =>
    import('./forms/shortcode_toolbar_component.js'),
  'tmp-post-lock-status': () => import('./admin/post_lock_status_component.ts'),
  'tmp-prevent-data-loss': () =>
    import('./forms/prevent_data_loss_component.ts'),
  'tmp-publishable-editor': () => import('./publishable/editor_component.js'),
  'tmp-publishable-timing-editor': () =>
    import('./publishable/timing_editor_component.ts'),
  'tmp-quote-button': () => import('./forms/shortcode_toolbar_component.js'),
  'tmp-remote-selector': () => import('./forms/remote_select_component.js'),
  'tmp-revealable-editor': () =>
    import('./forms/revealable_editor_component.js'),
  'tmp-section-break-button': () =>
    import('./forms/shortcode_toolbar_component.js'),
  'tmp-story-promo-button': () =>
    import('./forms/shortcode_toolbar_component.js'),
  'tmp-subhead-button': () => import('./forms/shortcode_toolbar_component.js'),
  'tmp-tag-list-editor': () => import('./forms/tag_list_editor_component.js'),
  'tmp-time-zone-input': () => import('./forms/time_zone_input_component.js'),
  'tmp-update-button': () => import('./forms/shortcode_toolbar_component.js'),
} as const satisfies CustomElementManifest;
